import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Key, Eye, EyeOff, Save, RefreshCw, CheckCircle, AlertTriangle, Trash2 } from 'lucide-react';
import { motion } from 'motion/react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { updateApiKeyCache, clearApiKeyCache } from '@/services/geminiService';

const APIKeyManager = () => {
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [keyStatus, setKeyStatus] = useState<'unknown' | 'valid' | 'invalid'>('unknown');
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadApiKey();

    // Set up a retry mechanism if APIs aren't available initially
    const retryTimer = setTimeout(() => {
      if (!apiKey && window.ezsite?.apis?.tablePage) {
        console.log('APIs now available, retrying API key load');
        loadApiKey();
      }
    }, 2000);

    return () => clearTimeout(retryTimer);
  }, []);

  const loadApiKey = async () => {
    setIsLoading(true);
    try {
      // Check if APIs are available
      if (!window.ezsite?.apis?.tablePage) {
        console.log('APIs not yet available, will retry...');
        setIsLoading(false);
        return;
      }

      // Fetch API key from database
      const response = await window.ezsite.apis.tablePage('15215', {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        {
          name: 'setting_key',
          op: 'Equal',
          value: 'gemini_api_key'
        }]
      });

      if (response.error) {
        console.error('Error loading API key:', response.error);
        toast({
          title: 'Error',
          description: 'Failed to load API key from database',
          variant: 'destructive'
        });
        return;
      }

      if (response.data?.List && response.data.List.length > 0) {
        const setting = response.data.List[0];
        const key = setting.setting_value || '';
        setApiKey(key);
        setKeyStatus(key ? 'valid' : 'unknown');
        // Update the service cache
        if (key) {
          updateApiKeyCache(key);
        }
        console.log('✅ API key loaded from database');
      } else {
        console.log('ℹ️ No API key found in database');
        setApiKey('');
        setKeyStatus('unknown');
      }
    } catch (error) {
      console.error('Error loading API key:', error);
      toast({
        title: 'Error',
        description: 'Failed to load API key configuration',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveApiKey = async () => {
    if (!apiKey.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a valid API key',
        variant: 'destructive'
      });
      return;
    }

    setIsSaving(true);
    try {
      // Check if APIs are available
      if (!window.ezsite?.apis?.tablePage || !window.ezsite?.apis?.tableUpdate || !window.ezsite?.apis?.tableCreate) {
        throw 'API not available';
      }

      // Check if setting exists
      const existingResponse = await window.ezsite.apis.tablePage('15215', {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        {
          name: 'setting_key',
          op: 'Equal',
          value: 'gemini_api_key'
        }]

      });

      const settingData = {
        setting_key: 'gemini_api_key',
        setting_value: apiKey.trim(),
        setting_description: 'Google Gemini API key for AI features',
        updated_by: user?.ID || 0,
        updated_at: new Date().toISOString()
      };

      if (existingResponse.data?.List && existingResponse.data.List.length > 0) {
        // Update existing setting
        const existingSetting = existingResponse.data.List[0];
        const updateResponse = await window.ezsite.apis.tableUpdate('15215', {
          ID: existingSetting.ID,
          ...settingData
        });

        if (updateResponse.error) {
          throw updateResponse.error;
        }
      } else {
        // Create new setting
        const createResponse = await window.ezsite.apis.tableCreate('15215', settingData);
        if (createResponse.error) {
          throw createResponse.error;
        }
      }

      // Update the cached API key
      updateApiKeyCache(apiKey.trim());
      setKeyStatus('valid');
      toast({
        title: 'Success',
        description: 'API key saved to database and is now available to all users'
      });
    } catch (error) {
      console.error('Error saving API key:', error);
      toast({
        title: 'Error',
        description: typeof error === 'string' ? error : 'Failed to save API key',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const testApiKey = async () => {
    if (!apiKey.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter an API key to test',
        variant: 'destructive'
      });
      return;
    }

    // Basic format validation for Gemini API keys
    const apiKeyPattern = /^AIza[0-9A-Za-z-_]{35}$/;
    if (!apiKeyPattern.test(apiKey.trim())) {
      setKeyStatus('invalid');
      toast({
        title: 'Error',
        description: 'Invalid API key format. Gemini API keys should start with "AIza" and be 39 characters long.',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      // Test with the current recommended Gemini model
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey.trim()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: 'Test' }]
          }]
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.candidates && data.candidates[0] && data.candidates[0].content) {
          setKeyStatus('valid');
          toast({
            title: 'Success',
            description: 'API key is valid and working!'
          });
        } else {
          console.error('Unexpected response format:', data);
          setKeyStatus('invalid');
          toast({
            title: 'Error',
            description: 'API key validation failed - unexpected response format',
            variant: 'destructive'
          });
        }
      } else {
        const errorData = await response.json().catch(() => null);
        console.error('API validation error:', errorData);

        setKeyStatus('invalid');

        if (response.status === 400) {
          toast({
            title: 'Error',
            description: 'Invalid API key format or request',
            variant: 'destructive'
          });
        } else if (response.status === 401) {
          toast({
            title: 'Error',
            description: 'API key is invalid or expired',
            variant: 'destructive'
          });
        } else if (response.status === 403) {
          toast({
            title: 'Error',
            description: 'API access forbidden. Check your quota or permissions.',
            variant: 'destructive'
          });
        } else if (response.status === 429) {
          toast({
            title: 'Error',
            description: 'API rate limit exceeded. Try again later.',
            variant: 'destructive'
          });
        } else {
          toast({
            title: 'Error',
            description: `API validation failed with status: ${response.status}`,
            variant: 'destructive'
          });
        }
      }
    } catch (error) {
      console.error('Error testing API key:', error);
      setKeyStatus('invalid');

      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        toast({
          title: 'Network Error',
          description: 'Unable to connect to Gemini API. Check your internet connection or try again later.',
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to test API key. Please try again.',
          variant: 'destructive'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const clearApiKey = async () => {
    setIsClearing(true);
    try {
      // Check if APIs are available
      if (!window.ezsite?.apis?.tablePage || !window.ezsite?.apis?.tableDelete) {
        throw 'API not available';
      }

      // Check if setting exists in database
      const existingResponse = await window.ezsite.apis.tablePage('15215', {
        PageNo: 1,
        PageSize: 1,
        Filters: [
        {
          name: 'setting_key',
          op: 'Equal',
          value: 'gemini_api_key'
        }]
      });

      if (existingResponse.data?.List && existingResponse.data.List.length > 0) {
        // Delete the setting from database
        const existingSetting = existingResponse.data.List[0];
        const deleteResponse = await window.ezsite.apis.tableDelete('15215', {
          ID: existingSetting.ID
        });

        if (deleteResponse.error) {
          throw deleteResponse.error;
        }
      }

      // Clear from cache and localStorage
      clearApiKeyCache();
      setApiKey('');
      setKeyStatus('unknown');

      toast({
        title: 'Success',
        description: 'API key cleared successfully'
      });
    } catch (error) {
      console.error('Error clearing API key:', error);
      toast({
        title: 'Error',
        description: typeof error === 'string' ? error : 'Failed to clear API key',
        variant: 'destructive'
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6" data-id="9qpine38t" data-path="src/components/superadmin/APIKeyManager.tsx">

      <Card data-id="3n2ay1gd1" data-path="src/components/superadmin/APIKeyManager.tsx">
        <CardHeader data-id="rr1vv32ka" data-path="src/components/superadmin/APIKeyManager.tsx">
          <CardTitle className="flex items-center gap-2" data-id="w0z56qskq" data-path="src/components/superadmin/APIKeyManager.tsx">
            <Key className="w-5 h-5" data-id="azinme827" data-path="src/components/superadmin/APIKeyManager.tsx" />
            Gemini API Key Management
          </CardTitle>
          <CardDescription data-id="n2nglemp6" data-path="src/components/superadmin/APIKeyManager.tsx">
            Configure the global Google Gemini API key that powers all AI features for all users in the application
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6" data-id="mprpy76zv" data-path="src/components/superadmin/APIKeyManager.tsx">
          {/* API Key Status */}
          <div className="flex items-center gap-2" data-id="reebrfm9q" data-path="src/components/superadmin/APIKeyManager.tsx">
            <span className="text-sm font-medium" data-id="f5430j77e" data-path="src/components/superadmin/APIKeyManager.tsx">Current Status:</span>
            {keyStatus === 'valid' &&
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200" data-id="erp8ybhud" data-path="src/components/superadmin/APIKeyManager.tsx">
                <CheckCircle className="w-3 h-3 mr-1" data-id="cd4kpz4e1" data-path="src/components/superadmin/APIKeyManager.tsx" />
                Valid
              </Badge>
            }
            {keyStatus === 'invalid' &&
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" data-id="7p216reb1" data-path="src/components/superadmin/APIKeyManager.tsx">
                <AlertTriangle className="w-3 h-3 mr-1" data-id="crrmaugt9" data-path="src/components/superadmin/APIKeyManager.tsx" />
                Invalid
              </Badge>
            }
            {keyStatus === 'unknown' &&
            <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200" data-id="n4ddno5ic" data-path="src/components/superadmin/APIKeyManager.tsx">
                Unknown
              </Badge>
            }
          </div>

          {/* API Key Input */}
          <div className="space-y-2" data-id="l9jdahbnw" data-path="src/components/superadmin/APIKeyManager.tsx">
            <Label htmlFor="apiKey" data-id="wbnb3qycr" data-path="src/components/superadmin/APIKeyManager.tsx">Gemini API Key</Label>
            <div className="relative" data-id="in6agu7nw" data-path="src/components/superadmin/APIKeyManager.tsx">
              <Input
                id="apiKey"
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your Google Gemini API key..."
                className="pr-20" data-id="kzwh442og" data-path="src/components/superadmin/APIKeyManager.tsx" />

              <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-3" data-id="407g79yjb" data-path="src/components/superadmin/APIKeyManager.tsx">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="h-7 w-7 p-0" data-id="ewf6h82aw" data-path="src/components/superadmin/APIKeyManager.tsx">

                  {showApiKey ?
                  <EyeOff className="w-4 h-4" data-id="89xrrlfsr" data-path="src/components/superadmin/APIKeyManager.tsx" /> :

                  <Eye className="w-4 h-4" data-id="sby00315c" data-path="src/components/superadmin/APIKeyManager.tsx" />
                  }
                </Button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3" data-id="zdoh6hygk" data-path="src/components/superadmin/APIKeyManager.tsx">
            <Button
              onClick={saveApiKey}
              disabled={isSaving || !apiKey.trim()}
              className="flex-1" data-id="a18vqijka" data-path="src/components/superadmin/APIKeyManager.tsx">

              {isSaving ?
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="rwjz2ygwp" data-path="src/components/superadmin/APIKeyManager.tsx" /> :

              <Save className="w-4 h-4 mr-2" data-id="og5ysqnd0" data-path="src/components/superadmin/APIKeyManager.tsx" />
              }
              Save API Key
            </Button>
            <Button
              variant="outline"
              onClick={testApiKey}
              disabled={isLoading || !apiKey.trim()}
              className="flex-1" data-id="ggvl1sbnp" data-path="src/components/superadmin/APIKeyManager.tsx">

              {isLoading ?
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="54uo77uv8" data-path="src/components/superadmin/APIKeyManager.tsx" /> :

              <CheckCircle className="w-4 h-4 mr-2" data-id="rg8ruwlim" data-path="src/components/superadmin/APIKeyManager.tsx" />
              }
              Test API Key
            </Button>
            <Button
              variant="destructive"
              onClick={clearApiKey}
              disabled={isClearing || !apiKey.trim()}
              className="flex-1" data-id="rvhh1rvjc" data-path="src/components/superadmin/APIKeyManager.tsx">

              {isClearing ?
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="1p5ztdh18" data-path="src/components/superadmin/APIKeyManager.tsx" /> :

              <Trash2 className="w-4 h-4 mr-2" data-id="mu4geig99" data-path="src/components/superadmin/APIKeyManager.tsx" />
              }
              Clear API Key
            </Button>
          </div>

          {/* Instructions */}
          <Alert data-id="0ie60spho" data-path="src/components/superadmin/APIKeyManager.tsx">
            <AlertTriangle className="h-4 w-4" data-id="dm6sh1nbf" data-path="src/components/superadmin/APIKeyManager.tsx" />
            <AlertDescription data-id="8w65h3lnt" data-path="src/components/superadmin/APIKeyManager.tsx">
              <strong data-id="7ei1mwmd8" data-path="src/components/superadmin/APIKeyManager.tsx">How to get your Gemini API key:</strong>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-sm" data-id="hgd8lgv95" data-path="src/components/superadmin/APIKeyManager.tsx">
                <li data-id="ggnkqy9ww" data-path="src/components/superadmin/APIKeyManager.tsx">Go to <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" data-id="fauuoky1e" data-path="src/components/superadmin/APIKeyManager.tsx">Google AI Studio</a></li>
                <li data-id="5eeymruud" data-path="src/components/superadmin/APIKeyManager.tsx">Sign in with your Google account</li>
                <li data-id="vjxv64me5" data-path="src/components/superadmin/APIKeyManager.tsx">Click "Create API key" and select a Google Cloud project</li>
                <li data-id="orlnkkpcf" data-path="src/components/superadmin/APIKeyManager.tsx">Copy the generated API key (starts with "AIza" and is 39 characters long)</li>
                <li data-id="h3i5qe0gk" data-path="src/components/superadmin/APIKeyManager.tsx">Paste it in the field above and click "Test API Key" to verify</li>
              </ol>
              <p className="mt-2 text-sm" data-id="miz7iftag" data-path="src/components/superadmin/APIKeyManager.tsx"><strong data-id="9yi9kc8d3" data-path="src/components/superadmin/APIKeyManager.tsx">Note:</strong> This API key is saved globally and will power all AI features for all users, including lesson plans, quizzes, plagiarism checking, and the chatbot.</p>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* AI Features Status */}
      <Card data-id="ov63rfpjz" data-path="src/components/superadmin/APIKeyManager.tsx">
        <CardHeader data-id="dz0cdoj5y" data-path="src/components/superadmin/APIKeyManager.tsx">
          <CardTitle data-id="8z8594qy3" data-path="src/components/superadmin/APIKeyManager.tsx">AI Features Status</CardTitle>
          <CardDescription data-id="0qras7hlv" data-path="src/components/superadmin/APIKeyManager.tsx">
            Overview of AI features that depend on the Gemini API key
          </CardDescription>
        </CardHeader>
        <CardContent data-id="gs79ftpl6" data-path="src/components/superadmin/APIKeyManager.tsx">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="e9oeh4l8p" data-path="src/components/superadmin/APIKeyManager.tsx">
            {[
            { name: 'Lesson Plan Generator', enabled: keyStatus === 'valid' },
            { name: 'Quiz Generator', enabled: keyStatus === 'valid' },
            { name: 'Plagiarism Checker', enabled: keyStatus === 'valid' },
            { name: 'Floating AI Chatbot', enabled: keyStatus === 'valid' }].
            map((feature) =>
            <div key={feature.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="p26auyimg" data-path="src/components/superadmin/APIKeyManager.tsx">
                <span className="text-sm font-medium" data-id="nfy03uar3" data-path="src/components/superadmin/APIKeyManager.tsx">{feature.name}</span>
                <Badge
                variant="outline"
                className={feature.enabled ?
                "bg-green-50 text-green-700 border-green-200" :
                "bg-gray-50 text-gray-700 border-gray-200"
                } data-id="u2a9ral7f" data-path="src/components/superadmin/APIKeyManager.tsx">

                  {feature.enabled ? 'Active' : 'Disabled'}
                </Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>);

};

export default APIKeyManager;